import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { TouchButton } from './TouchButton'
import { duration, easing, spring } from '../../lib/motion'
import styles from './ConfirmDialog.module.css'

export interface ConfirmOptions {
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'default' | 'danger'
}

interface ConfirmDialogProps extends ConfirmOptions {
  open: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Render-only del diálogo. Típicamente se usa via `<ConfirmProvider>` +
 * `useConfirm()`, pero queda exportado por si alguna ruta prefiere control
 * explícito.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  variant = 'default',
  onConfirm,
  onCancel
}: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [open, onCancel])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className={styles.overlay}
          onClick={onCancel}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: duration.fast, ease: easing.out }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-dialog-title"
        >
          <motion.div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.92, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 6 }}
            transition={spring.soft}
          >
            <h2 id="confirm-dialog-title" className={styles.title}>
              {title}
            </h2>
            <p className={styles.message}>{message}</p>
            <div className={styles.actions}>
              <TouchButton variant="ghost" onClick={onCancel}>
                {cancelLabel}
              </TouchButton>
              <TouchButton
                variant={variant === 'danger' ? 'danger' : 'primary'}
                onClick={onConfirm}
              >
                {confirmLabel}
              </TouchButton>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

interface PendingConfirm {
  options: ConfirmOptions
  resolve: (result: boolean) => void
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null)
  const resolverRef = useRef<((result: boolean) => void) | null>(null)

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      // Iter 2026-05-16 audit: si llega un segundo confirm() antes de
      // resolver el primero (doble-click rápido, race entre handlers),
      // el código viejo sobreescribía `pending` y dejaba la Promise
      // anterior colgada para siempre. Resolvemos la previa con `false`
      // (= "el usuario canceló implícitamente al abrir otro confirm")
      // antes de aceptar la nueva. Mejor que dejar la await colgada.
      const previousResolver = resolverRef.current
      if (previousResolver) {
        previousResolver(false)
      }
      resolverRef.current = resolve
      setPending({ options, resolve })
    })
  }, [])

  const handleConfirm = () => {
    pending?.resolve(true)
    resolverRef.current = null
    setPending(null)
  }
  const handleCancel = () => {
    pending?.resolve(false)
    resolverRef.current = null
    setPending(null)
  }

  const value = useMemo(() => confirm, [confirm])

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={pending !== null}
        title={pending?.options.title ?? ''}
        message={pending?.options.message ?? ''}
        confirmLabel={pending?.options.confirmLabel ?? 'Confirmar'}
        cancelLabel={pending?.options.cancelLabel ?? 'Cancelar'}
        variant={pending?.options.variant ?? 'default'}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm debe usarse dentro de <ConfirmProvider>')
  return ctx
}
